import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { clearAllAuthData } from '../../services/authService';
import { getConfig } from '../../config';
import Loading from '../../components/Loading';
import './LoginPage.css';

export function LoginPage() {
  const { login, isLoading: authLoading } = useAuth();
  const { setSelectedApplication } = useApp();
  const [selectedAppId, setSelectedAppId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [hasStaleToken, setHasStaleToken] = useState(false);

  const config = getConfig();
  const hasPresetAppId = !!config.applicationId;

  // Check for stale token on mount and set preset app ID
  useEffect(() => {
    const storedAuth = localStorage.getItem('dt_platform_auth');
    const storedClientId = localStorage.getItem('dt_platform_client_id');
    const uiStoredClientId = localStorage.getItem('platform_app_clientId');

    // Only show stale session warning if there's actual auth data (not just a pending client ID)
    if (storedAuth) {
      setHasStaleToken(true);
    }

    // Clear any orphaned client ID (from failed OAuth attempts)
    if (storedClientId && !storedAuth) {
      localStorage.removeItem('dt_platform_client_id');
      localStorage.removeItem('dt_platform_pkce');
      localStorage.removeItem('dt_platform_pre_auth_uri');
      console.log('Cleared orphaned OAuth data');
    }

    // If applicationId is preset in config, use it directly
    if (config.applicationId) {
      setSelectedAppId(config.applicationId);
    } else if (uiStoredClientId) {
      // Prefill from UI-level stored client id to make login quicker after logout
      setSelectedAppId(uiStoredClientId);
      // eslint-disable-next-line no-console
      console.debug('[Login] Pre-filled Application ID from storage:', uiStoredClientId);
    }
  }, [config.applicationId]);

  const handleLogin = async () => {
    if (!selectedAppId) {
      setError('Please enter an Application ID');
      return;
    }

    setError(null);

    // This will redirect to the Invicara login page
    await login(selectedAppId);
  };

  const handleClearSession = () => {
    clearAllAuthData();
    setHasStaleToken(false);
    setError(null);
    // Reload the page to start fresh
    window.location.reload();
  };

  if (authLoading) {
    return <Loading message="Authenticating..." fullScreen />;
  }

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="login-gradient"></div>
        <div className="login-pattern"></div>
      </div>

      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <i className="fas fa-cube"></i>
            </div>
            <h1 className="login-title">DT Platform</h1>
            <p className="login-subtitle">Sign in to continue to your workspace</p>
          </div>

          {error && (
            <div className="login-error">
              <i className="fas fa-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {hasStaleToken && (
            <div className="login-warning">
              <i className="fas fa-info-circle"></i>
              <div>
                <p>A previous session was detected.</p>
                <button
                  className="clear-session-link"
                  onClick={handleClearSession}
                >
                  Clear session and start fresh
                </button>
              </div>
            </div>
          )}

          {!hasPresetAppId && (
            <div className="login-form">
              <div className="form-group">
                <label htmlFor="application-id">Application ID</label>
                <input
                  type="text"
                  id="application-id"
                  value={selectedAppId}
                  onChange={(e) => setSelectedAppId(e.target.value)}
                  className="form-input"
                  placeholder="Enter your application ID..."
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                />
                <p className="form-hint">
                  Enter the Application ID (Client ID) provided by your administrator
                </p>
              </div>
            </div>
          )}

          {hasPresetAppId && (
            <div className="preset-app-info">
              <i className="fas fa-check-circle"></i>
              <span>Application configured: <strong>{config.applicationId}</strong></span>
            </div>
          )}

          <button
            className="login-button"
            onClick={handleLogin}
            disabled={!selectedAppId}
          >
            <span>Sign in with Invicara</span>
            <i className="fas fa-external-link-alt"></i>
          </button>

          <div className="login-oauth-info">
            <i className="fas fa-shield-alt"></i>
            <p>
              You'll be redirected to Invicara's secure login page to enter your credentials.
              Your password is never shared with this application.
            </p>
          </div>

          <div className="login-footer">
            <p>
              By signing in, you agree to our{' '}
              <a href="#terms">Terms of Service</a> and{' '}
              <a href="#privacy">Privacy Policy</a>
            </p>
          </div>
        </div>

        <div className="login-info">
          <div className="info-item">
            <i className="fas fa-shield-alt"></i>
            <span>OAuth 2.0 PKCE authentication</span>
          </div>
          <div className="info-item">
            <i className="fas fa-lock"></i>
            <span>Credentials secured by Invicara</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
